import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import {
  formatNumber,
  getDisplayDate,
  getFormattedEndDateString,
  getFormattedStartDateString,
} from '../../../utils'
import { createMonthlyBill } from '../../../services/monthlyBills'
import { BulkInvoicePreviewItem } from './types'
import {
  bulkInvoiceTableHeaderSx,
  bulkInvoiceTableSeparatorSx,
} from './bulkInvoiceTableStyles'

const TEXT_MAX_LEN = 30

const cellPaddingSx = { px: 1.5, py: 1.25 }

const firstColumnCellSx = {
  pl: 3,
  pr: 1.5,
}

const lastColumnCellSx = {
  pl: 1.5,
  pr: 3,
}

const bodyCellSx = {
  ...cellPaddingSx,
  borderBottom: '1px solid',
  borderColor: 'grey.200',
  borderLeft: 'none',
  borderRight: 'none',
  fontSize: '0.875rem',
}

const partySiteColumnSx = {
  maxWidth: 200,
}

const actionCellSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 0.5,
  width: '100%',
}

const headerCellSx = {
  ...cellPaddingSx,
  fontSize: '0.8125rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  ...bulkInvoiceTableHeaderSx,
}

const snColumnSx = { width: 44, minWidth: 44 }

function getValidErrorMessage(message?: string | null): string | null {
  const trimmed = message?.trim()
  return trimmed ? trimmed : null
}

function hasPreviewError(message?: string | null): boolean {
  return getValidErrorMessage(message) != null
}

function isCreatableRow(row: PreviewRow): boolean {
  return row.status === 'pending' && !hasPreviewError(row.errorMessage)
}

function truncateText(value: string | null | undefined, maxLength = TEXT_MAX_LEN): string {
  const text = value ?? ''
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}…`
}

function renderTruncatedCell(value: string) {
  const display = truncateText(value)
  if (display === value) {
    return display
  }
  return (
    <Tooltip title={value} arrow placement="top">
      <Typography
        component="span"
        variant="body2"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </Typography>
    </Tooltip>
  )
}

type RowCreateStatus = 'pending' | 'creating' | 'success' | 'failed'

interface PreviewRow extends BulkInvoicePreviewItem {
  rowId: string
  status: RowCreateStatus
  errorMessage?: string
}

export interface CreateInvoicesStepHandle {
  getInvoiceCount: () => number
  startCreate: () => Promise<void>
  isCreating: () => boolean
  isComplete: () => boolean
}

export interface CreateInvoicesStepProps {
  items: BulkInvoicePreviewItem[]
  billFormat: string
  onInvoiceCountChange?: (count: number) => void
  onCreatingChange?: (creating: boolean) => void
  onCompleteChange?: (complete: boolean) => void
}

function buildPreviewRows(items: BulkInvoicePreviewItem[]): PreviewRow[] {
  return items.map((item, index) => {
    const previewError = getValidErrorMessage(item.errorMessage)
    return {
      ...item,
      rowId: `${item.siteId}-${item.invNo}-${index}`,
      status: 'pending' as RowCreateStatus,
      ...(previewError && { errorMessage: previewError }),
    }
  })
}

function formatPreviewDateForApi(dateString: string, kind: 'start' | 'end' | 'create') {
  const date = new Date(dateString)
  if (kind === 'start') {
    return getFormattedStartDateString(date, false)
  }
  return getFormattedEndDateString(date, false)
}

function formatDisplayInvoiceNo(
  displayInvoiceNo: string | undefined,
  isGst?: boolean
): string {
  const no = displayInvoiceNo?.trim()
  if (!no) {
    return ''
  }
  if (isGst === true) {
    return no
  }
  return `${no} (No-Gst)`
}

export const CreateInvoicesStep = forwardRef<
  CreateInvoicesStepHandle,
  CreateInvoicesStepProps
>(function CreateInvoicesStep(
  { items, billFormat, onInvoiceCountChange, onCreatingChange, onCompleteChange },
  ref
) {
  const [rows, setRows] = useState<PreviewRow[]>(() => buildPreviewRows(items))
  const [isCreating, setIsCreating] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map())
  const creatingRowId = rows.find((row) => row.status === 'creating')?.rowId

  useEffect(() => {
    setRows(buildPreviewRows(items))
    setIsCreating(false)
    setIsComplete(false)
  }, [items, billFormat])

  const creatableCount = rows.filter((row) => !hasPreviewError(row.errorMessage)).length

  useEffect(() => {
    onInvoiceCountChange?.(creatableCount)
  }, [creatableCount, onInvoiceCountChange])

  useEffect(() => {
    if (!creatingRowId) return
    const rowEl = rowRefs.current.get(creatingRowId)
    rowEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [creatingRowId])

  useEffect(() => {
    onCreatingChange?.(isCreating)
  }, [isCreating, onCreatingChange])

  useEffect(() => {
    onCompleteChange?.(isComplete)
  }, [isComplete, onCompleteChange])

  const handleRemoveRow = (rowId: string) => {
    if (isCreating) return
    setRows((current) => current.filter((row) => row.rowId !== rowId))
  }

  const updateRowStatus = (
    rowId: string,
    status: RowCreateStatus,
    options?: { errorMessage?: string }
  ) => {
    setRows((current) =>
      current.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              status,
              ...(options?.errorMessage !== undefined && {
                errorMessage: options.errorMessage,
              }),
            }
          : row
      )
    )
  }

  const createInvoiceForRow = async (
    row: PreviewRow
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await createMonthlyBill({
        accountId: row.siteId,
        fromDate: formatPreviewDateForApi(row.from, 'start'),
        toDate: formatPreviewDateForApi(row.to, 'end'),
        createDate: formatPreviewDateForApi(row.createDate, 'create'),
        invoiceNo: Number(row.invNo),
        discount: 0,
        billFormat,
      })
      if (response?.data?.success === true) {
        return { success: true }
      }
      return {
        success: false,
        message: response?.data?.message || 'Failed to create invoice.',
      }
    } catch (err: any) {
      return {
        success: false,
        message:
          err?.response?.data?.message ||
          err?.message ||
          'Failed to create invoice.',
      }
    }
  }

  const startCreate = async () => {
    if (isCreating || rows.length === 0) return

    setIsCreating(true)
    setIsComplete(false)

    const pendingRows = rows.filter(isCreatableRow)
    for (const row of pendingRows) {
      updateRowStatus(row.rowId, 'creating')
      const result = await createInvoiceForRow(row)
      updateRowStatus(row.rowId, result.success ? 'success' : 'failed', {
        errorMessage: result.message,
      })
    }

    setIsCreating(false)
    setIsComplete(true)
  }

  useImperativeHandle(ref, () => ({
    getInvoiceCount: () => creatableCount,
    startCreate,
    isCreating: () => isCreating,
    isComplete: () => isComplete,
  }))

  const formatPeriod = (from: string, to: string) =>
    `${getDisplayDate(from)} – ${getDisplayDate(to)}`

  const renderActionCell = (row: PreviewRow) => {
    if (row.status === 'creating') {
      return (
        <Box sx={actionCellSx}>
          <CircularProgress size={22} />
        </Box>
      )
    }
    if (row.status === 'success') {
      return (
        <Box sx={actionCellSx}>
          <CheckCircleIcon
            sx={{
              color: 'success.main',
              fontSize: 24,
              animation: 'previewSuccessPop 0.45s ease-out',
              '@keyframes previewSuccessPop': {
                '0%': { transform: 'scale(0.4)', opacity: 0 },
                '70%': { transform: 'scale(1.15)', opacity: 1 },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          />
        </Box>
      )
    }
    if (row.status === 'failed') {
      return (
        <Box sx={actionCellSx}>
          <Tooltip title={row.errorMessage || 'Failed to create invoice.'} arrow>
            <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
          </Tooltip>
        </Box>
      )
    }
    const previewError = getValidErrorMessage(row.errorMessage)
    if (previewError) {
      return (
        <Box sx={actionCellSx}>
          <Tooltip title={previewError} arrow>
            <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
          </Tooltip>
        </Box>
      )
    }
    return (
      <Box sx={actionCellSx}>
        <IconButton
          size="small"
          onClick={() => handleRemoveRow(row.rowId)}
          disabled={isCreating}
          aria-label="Remove from preview"
          sx={{ p: 0.25 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    )
  }

  if (rows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2, px: 2 }}>
        No invoices in preview.
      </Typography>
    )
  }

  return (
    <>
      <Divider sx={bulkInvoiceTableSeparatorSx} />
      <TableContainer
        sx={{
          maxHeight: 'calc(100vh - 280px)',
          overflow: 'auto',
          m: 0,
        }}
      >
      <Table aria-label="bulk invoices preview" stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellSx, ...firstColumnCellSx, ...snColumnSx }}>
              S.N.
            </TableCell>
            <TableCell sx={{ ...headerCellSx }}>Inv No</TableCell>
            <TableCell sx={{ ...headerCellSx, ...partySiteColumnSx }}>Party</TableCell>
            <TableCell sx={{ ...headerCellSx, ...partySiteColumnSx }}>Site</TableCell>
            <TableCell sx={{ ...headerCellSx, minWidth: 140, whiteSpace: 'nowrap' }}>
              From – To
            </TableCell>
            <TableCell align="right" sx={headerCellSx}>
              Taxable
            </TableCell>
            <TableCell align="right" sx={headerCellSx}>
              Total
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...headerCellSx, ...lastColumnCellSx, minWidth: 88 }}
            >
              Action
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={row.rowId}
              ref={(el) => {
                if (el) {
                  rowRefs.current.set(row.rowId, el)
                } else {
                  rowRefs.current.delete(row.rowId)
                }
              }}
              sx={{
                backgroundColor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&:last-child td': {
                  borderBottom: 'none',
                },
              }}
            >
              <TableCell sx={{ ...bodyCellSx, ...firstColumnCellSx, ...snColumnSx }}>
                {index + 1}
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 500 }}>
                {formatDisplayInvoiceNo(row.displayInvoiceNo, row.isGst)}
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, ...partySiteColumnSx }}>
                {renderTruncatedCell(row.party)}
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, ...partySiteColumnSx }}>
                {renderTruncatedCell(row.site)}
              </TableCell>
              <TableCell
                sx={{ ...bodyCellSx, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {formatPeriod(row.from, row.to)}
              </TableCell>
              <TableCell
                sx={{
                  ...bodyCellSx,
                  fontVariantNumeric: 'tabular-nums',
                  color: 'text.secondary',
                }}
                align="right"
              >
                {formatNumber(row.taxableAmount)}
              </TableCell>
              <TableCell
                sx={{
                  ...bodyCellSx,
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                }}
                align="right"
              >
                {formatNumber(row.invoiceTotal)}
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, ...lastColumnCellSx }} align="right">
                {renderActionCell(row)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  )
})

export default CreateInvoicesStep
